import { Component, ReactNode } from 'react'
import styles from 'components/features/socialFeed/FeedPostList/FeedPostList.styl'
import { PostUid, FeedResource } from 'interfaces/feed'
import Anchor from 'components/atoms/navigation/Anchor'
import { getFeedPostAnchorId } from 'helpers/feedHelpers'
import Button from 'components/features/pfm/Button/Button'
import { FeedPostListZeroState } from 'components/features/socialFeed/FeedPostList/FeedPostListZeroState'
import { InfiniteWindowScroll } from 'components/features/pfm/InfiniteScroll/InfiniteWindowScroll'
import Deferred from 'components/utils/Deferred'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { FeedPostListErrorState } from 'components/features/socialFeed/FeedPostList/FeedPostListErrorState'
import { NOT_FOUND_STATUS_CODE } from 'constants/ajax'
import { PostTypeJunction } from 'components/features/pfm/PostTypeJunction/PostTypeJunction'

const DeferredSpinner = Deferred(Spinner, 300)

const INFINITE_SCROLL_LOAD_THRESHOLD = 1200

type AdditionalBlockKind = 'trending-tags'
type FeedPostListAdditionalBlocks = Partial<Record<AdditionalBlockKind, ReactNode>>

export type FeedPostListStateProps = {
  itemIds: PostUid[]
  hasMore: boolean
  isLoading: boolean
  errorCode: number | null
}

export type FeedPostListDispatchProps = {
  onLoadMore: () => void
}

export type FeedPostListOwnProps = {
  resource?: FeedResource
  additionalBlocks?: FeedPostListAdditionalBlocks
}

export type FeedPostListProps =
  & FeedPostListStateProps
  & FeedPostListDispatchProps
  & FeedPostListOwnProps

class FeedPostList extends Component<FeedPostListProps> {
  private itemsIdsCache: Record<string, boolean> = {}

  render () {
    const {
      itemIds,
      isLoading,
      hasMore,
      onLoadMore,
      errorCode,
      resource,
      additionalBlocks,
    } = this.props

    if (!itemIds.length && !isLoading) {
      if (errorCode === null || errorCode === NOT_FOUND_STATUS_CODE) {
        return (
          <>
            <FeedPostListZeroState resource={ resource } />
            { additionalBlocks && additionalBlocks['trending-tags'] }
          </>
        )
      }

      return (
        <>
          <FeedPostListErrorState onTryAgain={ onLoadMore } />
          { additionalBlocks && additionalBlocks['trending-tags'] }
        </>
      )
    }

    const resourceType = resource ? resource.type : null

    this.itemsIdsCache = {}
    const postsChildren = itemIds.map((itemId, idx) => {
      if (additionalBlocks) {
        if (idx === 2 && additionalBlocks['trending-tags']) {
          return additionalBlocks['trending-tags']
        }
      }

      return (
        <div className={ styles.post } key={ this.getUniqKey(itemId) }>
          <Anchor placement={ getFeedPostAnchorId(itemId) }>
            <PostTypeJunction postUid={ itemId } singlePostPage={ resourceType === 'post' } />
          </Anchor>
        </div>
      )
    })

    const loadMoreButton = hasMore && errorCode !== null
      ? (
        <div className={ styles.loadMoreButtonWrapper }>
          <Button
            size='medium'
            theme='blue'
            isSubmitting={ isLoading }
            onClick={ onLoadMore }
          >
            Load More Posts
          </Button>
        </div>
      )
      : null

    return (
      <InfiniteWindowScroll
        className={ styles.container }
        hasMore={ hasMore && errorCode === null }
        isLoading={ isLoading }
        onLoadMore={ onLoadMore }
        threshold={ INFINITE_SCROLL_LOAD_THRESHOLD }
      >
        { postsChildren }
        { loadMoreButton }
        { hasMore && errorCode === null && (
          <div className={ styles.preloader }>
            { isLoading && <DeferredSpinner /> }
          </div>
        )}
      </InfiniteWindowScroll>
    )
  }

  private getUniqKey (itemId: string): string {
    const isExists = this.itemsIdsCache[itemId]
    if (isExists) {
      return this.getUniqKey(`${ itemId }+`)
    }
    this.itemsIdsCache[itemId] = true
    return itemId
  }
}

export default FeedPostList
